import { useState } from 'react';
import { AdminSidebar } from '../../components/admin/AdminSidebar';
import { ToggleLeft } from 'lucide-react';
import { useFeatureFlagStore } from '@shared/state';
import { RECALIBRATION_FLAG_META, type RecalibrationFlagKey } from '@shared/constants';
import { setFeatureFlags } from '@shared/services/feature-flags';

export function AdminFeatureFlags() {
  const flags = useFeatureFlagStore((s) => s.flags);
  const loaded = useFeatureFlagStore((s) => s.loaded);
  const [savingKey, setSavingKey] = useState<RecalibrationFlagKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function toggle(key: RecalibrationFlagKey, next: boolean) {
    setSavingKey(key);
    setError(null);
    try {
      await setFeatureFlags({ [key]: next });
      // The store updates live from the Firestore doc subscription.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update flag');
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      <div className="flex">
        <AdminSidebar />
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <h1 className="font-heading text-2xl font-bold mb-1">Feature Flags</h1>
          <p className="text-content-muted text-sm mb-6">
            Toggle recalibration and Coach surfaces. Changes apply globally within ~30 seconds
            (server cache) and immediately for clients.
          </p>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm">
              {error}
            </div>
          )}

          <div className="rounded-2xl bg-dark-surface border border-dark-border divide-y divide-dark-border">
            {RECALIBRATION_FLAG_META.map(({ key, label, description }) => {
              const on = flags[key];
              const busy = savingKey === key;
              return (
                <div key={key} className="flex items-center gap-4 p-4">
                  <ToggleLeft size={18} className={on ? 'text-success' : 'text-content-dim'} />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-sm font-semibold text-content">
                      {label}
                      <span className="ml-2 font-mono text-[10px] text-content-dim">{key}</span>
                    </div>
                    <p className="text-xs text-content-muted">{description}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    aria-label={`Toggle ${label}`}
                    disabled={busy || !loaded}
                    onClick={() => toggle(key, !on)}
                    className={`relative w-11 h-6 rounded-full transition-colors shrink-0 cursor-pointer disabled:opacity-50 ${
                      on ? 'bg-success/70' : 'bg-dark-border'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                        on ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>

          <p className="mt-4 text-[11px] text-content-dim">
            Coach, Counterfactual, and Auto-pilot stay off until Lemon Squeezy billing is wired
            (see <span className="font-mono">purchaseCoachSeats</span>). Enabling Coach here surfaces
            the admin seat UI, but seat purchasing remains blocked until billing is configured.
          </p>
        </main>
      </div>
    </div>
  );
}
